import React, { useEffect, useState } from "react";
import styled from "styled-components";

const DEFAULT_SUMMARY_URL =
  process.env.REACT_APP_UPTIME_SUMMARY_URL ||
  "https://aayushg.com/lipoker/uptime/summary.json";

const REFRESH_MS = 60 * 1000;

const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return `${Number(value).toFixed(2)}%`;
};

const formatLatency = (value) => {
  if (value === null || value === undefined) {
    return "--";
  }
  return `${Math.round(value)} ms`;
};

const formatTimestamp = (value) => {
  if (!value) {
    return "--";
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) {
    return "--";
  }

  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (!remainingMinutes) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

const minutesSince = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }

  return Math.round((Date.now() - timestamp) / 60000);
};

const getStatusTone = (status) => {
  if (status === "up") {
    return "up";
  }
  if (status === "degraded") {
    return "degraded";
  }
  return "down";
};

const getDailyTone = (uptimePct) => {
  if (uptimePct === null || uptimePct === undefined) {
    return "missing";
  }
  if (uptimePct >= 99.95) {
    return "perfect";
  }
  if (uptimePct >= 99) {
    return "stable";
  }
  if (uptimePct >= 95) {
    return "shaky";
  }
  return "down";
};

const buildSourceSummary = (source) => {
  if (!source) {
    return "External monitor";
  }

  const parts = [];
  if (source.provider) {
    parts.push(source.provider);
  }
  if (source.repo && source.branch) {
    parts.push(`${source.repo}@${source.branch}`);
  } else if (source.repo) {
    parts.push(source.repo);
  }
  if (source.intervalMinutes) {
    parts.push(`every ${source.intervalMinutes} min`);
  }
  return parts.join(" · ") || "External monitor";
};

const isLinkableTarget = (value) => /^https?:\/\//.test(value || "");

const UptimePage = () => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "LiPoker Uptime";
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      try {
        const cacheBuster = Math.floor(Date.now() / REFRESH_MS);
        const response = await fetch(
          `${DEFAULT_SUMMARY_URL}?v=${cacheBuster}`,
          {
            cache: "no-store",
          }
        );

        if (!response.ok) {
          throw new Error(`Monitor feed returned HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!cancelled) {
          setSummary(data);
          setError(null);
          setLoading(false);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
          setLoading(false);
        }
      }
    };

    loadSummary();
    const interval = window.setInterval(loadSummary, REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const staleMinutes = minutesSince(summary?.generatedAt);
  const sourceSummary = buildSourceSummary(summary?.source);
  const services = summary?.services || [];

  return (
    <PageShell>
      <Backdrop />
      <Content>
        <Hero>
          <Eyebrow>Independent Monitoring</Eyebrow>
          <Title>LiPoker uptime</Title>
          <Subtitle>
            Frontend, backend, and database availability from checks that run
            outside the production cluster.
          </Subtitle>
          <HeroMeta>
            <MetaCard>
              <MetaLabel>Monitor source</MetaLabel>
              <MetaValue>{sourceSummary}</MetaValue>
            </MetaCard>
            <MetaCard>
              <MetaLabel>Last publish</MetaLabel>
              <MetaValue>{formatTimestamp(summary?.generatedAt)}</MetaValue>
            </MetaCard>
            <MetaCard>
              <MetaLabel>Feed status</MetaLabel>
              <MetaValue>
                {loading
                  ? "Loading"
                  : staleMinutes !== null && staleMinutes > 15
                  ? `Stale by ${staleMinutes} min`
                  : "Current"}
              </MetaValue>
            </MetaCard>
          </HeroMeta>
        </Hero>

        {loading ? (
          <StateCard>Loading uptime feed…</StateCard>
        ) : error ? (
          <StateCard>
            <StateTitle>Monitor feed unavailable</StateTitle>
            <StateText>{error}</StateText>
            <StateText>
              Expected feed:{" "}
              <StateLink href={DEFAULT_SUMMARY_URL} target="_blank" rel="noreferrer">
                {DEFAULT_SUMMARY_URL}
              </StateLink>
            </StateText>
          </StateCard>
        ) : (
          <ServiceGrid>
            {services.map((service) => {
              const incidents = service.incidents || [];
              const daily = service.daily || [];
              const tone = getStatusTone(service.status);

              return (
                <ServiceCard key={service.key || service.name}>
                  <ServiceHeader>
                    <div>
                      <ServiceName>{service.name}</ServiceName>
                      <ServiceDescription>
                        {service.description || service.method}
                      </ServiceDescription>
                    </div>
                    <StatusBadge tone={tone}>
                      <StatusDot tone={tone} />
                      {service.status || "unknown"}
                    </StatusBadge>
                  </ServiceHeader>

                  <ServiceMetaRow>
                    <MetaPair>
                      <MetaLabel>Target</MetaLabel>
                      {isLinkableTarget(service.target) ? (
                        <ServiceLink
                          href={service.target}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {service.target}
                        </ServiceLink>
                      ) : (
                        <MetaValue>{service.target || "--"}</MetaValue>
                      )}
                    </MetaPair>
                    <MetaPair>
                      <MetaLabel>Check</MetaLabel>
                      <MetaValue>{service.method || "--"}</MetaValue>
                    </MetaPair>
                    <MetaPair>
                      <MetaLabel>Last check</MetaLabel>
                      <MetaValue>{formatTimestamp(service.checkedAt)}</MetaValue>
                    </MetaPair>
                    <MetaPair>
                      <MetaLabel>Latency</MetaLabel>
                      <MetaValue>{formatLatency(service.latestLatencyMs)}</MetaValue>
                    </MetaPair>
                  </ServiceMetaRow>

                  <WindowGrid>
                    <WindowCard>
                      <MetaLabel>24h</MetaLabel>
                      <WindowValue>
                        {formatPercent(service.uptimePct?.["24h"])}
                      </WindowValue>
                    </WindowCard>
                    <WindowCard>
                      <MetaLabel>7d</MetaLabel>
                      <WindowValue>
                        {formatPercent(service.uptimePct?.["7d"])}
                      </WindowValue>
                    </WindowCard>
                    <WindowCard>
                      <MetaLabel>30d</MetaLabel>
                      <WindowValue>
                        {formatPercent(service.uptimePct?.["30d"])}
                      </WindowValue>
                    </WindowCard>
                  </WindowGrid>

                  <HistoryBlock>
                    <BlockTitle>Recent daily availability</BlockTitle>
                    <HistoryBars>
                      {daily.map((day) => (
                        <HistoryBar
                          key={day.date}
                          tone={getDailyTone(day.uptimePct)}
                          title={`${day.date}: ${formatPercent(day.uptimePct)}`}
                        />
                      ))}
                    </HistoryBars>
                    <HistoryLegend>
                      <LegendSwatch tone="perfect" />
                      <LegendText>100%</LegendText>
                      <LegendSwatch tone="stable" />
                      <LegendText>99%+</LegendText>
                      <LegendSwatch tone="shaky" />
                      <LegendText>95%+</LegendText>
                      <LegendSwatch tone="down" />
                      <LegendText>{"<95%"}</LegendText>
                    </HistoryLegend>
                  </HistoryBlock>

                  <HistoryBlock>
                    <BlockTitle>Latest result</BlockTitle>
                    <IncidentCard>
                      <IncidentMeta>
                        <strong>{service.latestReason || "No recent result"}</strong>
                      </IncidentMeta>
                    </IncidentCard>
                  </HistoryBlock>

                  <HistoryBlock>
                    <BlockTitle>Recent incidents</BlockTitle>
                    {incidents.length ? (
                      incidents.map((incident, index) => (
                        <IncidentCard key={`${service.key}-${index}`}>
                          <IncidentMeta>
                            <strong>
                              {incident.endedAt ? "Resolved" : "Open incident"}
                            </strong>
                            <span>{formatDuration(incident.durationMinutes)}</span>
                          </IncidentMeta>
                          <IncidentText>
                            {formatTimestamp(incident.startedAt)}
                            {incident.endedAt
                              ? ` to ${formatTimestamp(incident.endedAt)}`
                              : " to now"}
                          </IncidentText>
                          <IncidentText>
                            {incident.reason || "Unavailable"}
                          </IncidentText>
                        </IncidentCard>
                      ))
                    ) : (
                      <EmptyState>No incidents recorded in the retained history.</EmptyState>
                    )}
                  </HistoryBlock>
                </ServiceCard>
              );
            })}
          </ServiceGrid>
        )}
      </Content>
    </PageShell>
  );
};

const PageShell = styled.main`
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(211, 121, 75, 0.14), transparent 34%),
    radial-gradient(circle at bottom right, rgba(19, 91, 86, 0.18), transparent 30%),
    linear-gradient(180deg, #f6f0e8 0%, #efe5d7 45%, #e8dccd 100%);
  color: #1d2019;
  position: relative;
  overflow: hidden;
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(29, 32, 25, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(29, 32, 25, 0.04) 1px, transparent 1px);
  background-size: 32px 32px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.75), transparent);
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 72px 20px 96px;
`;

const Hero = styled.section`
  margin-bottom: 32px;
`;

const Eyebrow = styled.div`
  color: #8f4f2f;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.18em;
  margin-bottom: 12px;
  text-transform: uppercase;
`;

const Title = styled.h1`
  font-size: clamp(48px, 9vw, 96px);
  line-height: 0.95;
  margin: 0 0 16px;
  text-transform: uppercase;
`;

const Subtitle = styled.p`
  color: rgba(29, 32, 25, 0.78);
  font-size: 20px;
  line-height: 1.5;
  margin: 0;
  max-width: 720px;
`;

const HeroMeta = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  margin-top: 28px;
`;

const MetaCard = styled.div`
  backdrop-filter: blur(10px);
  background: rgba(255, 252, 247, 0.72);
  border: 1px solid rgba(29, 32, 25, 0.08);
  border-radius: 18px;
  box-shadow: 0 18px 60px rgba(57, 38, 18, 0.08);
  padding: 18px 20px;
`;

const StateCard = styled.div`
  backdrop-filter: blur(10px);
  background: rgba(255, 252, 247, 0.82);
  border: 1px solid rgba(29, 32, 25, 0.08);
  border-radius: 22px;
  box-shadow: 0 18px 60px rgba(57, 38, 18, 0.08);
  padding: 24px;
`;

const StateTitle = styled.h2`
  font-size: 28px;
  margin: 0 0 12px;
`;

const StateText = styled.p`
  color: rgba(29, 32, 25, 0.76);
  line-height: 1.6;
  margin: 0 0 10px;
`;

const StateLink = styled.a`
  color: #8f4f2f;
  word-break: break-word;
`;

const ServiceGrid = styled.section`
  display: grid;
  gap: 20px;
`;

const ServiceCard = styled.article`
  backdrop-filter: blur(10px);
  background: rgba(255, 252, 247, 0.78);
  border: 1px solid rgba(29, 32, 25, 0.08);
  border-radius: 24px;
  box-shadow: 0 18px 60px rgba(57, 38, 18, 0.08);
  padding: 24px;
`;

const ServiceHeader = styled.div`
  align-items: start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ServiceName = styled.h2`
  font-size: 34px;
  line-height: 1;
  margin: 0 0 8px;
  text-transform: uppercase;
`;

const ServiceDescription = styled.p`
  color: rgba(29, 32, 25, 0.7);
  line-height: 1.5;
  margin: 0;
  max-width: 720px;
`;

const StatusBadge = styled.div`
  align-items: center;
  background: ${({ tone }) =>
    tone === "up"
      ? "rgba(46, 113, 84, 0.13)"
      : tone === "degraded"
      ? "rgba(196, 141, 42, 0.14)"
      : "rgba(161, 57, 45, 0.14)"};
  border: 1px solid
    ${({ tone }) =>
      tone === "up"
        ? "rgba(46, 113, 84, 0.26)"
        : tone === "degraded"
        ? "rgba(196, 141, 42, 0.3)"
        : "rgba(161, 57, 45, 0.3)"};
  border-radius: 999px;
  color: ${({ tone }) =>
    tone === "up" ? "#215f47" : tone === "degraded" ? "#996600" : "#8b2f23"};
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  gap: 10px;
  padding: 10px 14px;
  text-transform: capitalize;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  background: ${({ tone }) =>
    tone === "up" ? "#2e7154" : tone === "degraded" ? "#c48d2a" : "#a1392d"};
  border-radius: 50%;
  display: block;
  height: 10px;
  width: 10px;
`;

const ServiceMetaRow = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin-bottom: 20px;
`;

const MetaPair = styled.div`
  min-width: 0;
`;

const MetaLabel = styled.div`
  color: rgba(29, 32, 25, 0.55);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  margin-bottom: 6px;
  text-transform: uppercase;
`;

const MetaValue = styled.div`
  font-size: 16px;
  line-height: 1.4;
`;

const ServiceLink = styled.a`
  color: #8f4f2f;
  display: inline-block;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const WindowGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  margin-bottom: 22px;
`;

const WindowCard = styled.div`
  background: rgba(29, 32, 25, 0.04);
  border-radius: 16px;
  padding: 16px;
`;

const WindowValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
`;

const HistoryBlock = styled.section`
  margin-top: 20px;
`;

const BlockTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 12px;
`;

const HistoryBars = styled.div`
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(auto-fill, minmax(12px, 1fr));
`;

const HistoryBar = styled.div`
  background: ${({ tone }) =>
    tone === "perfect"
      ? "#2e7154"
      : tone === "stable"
      ? "#7aa56f"
      : tone === "shaky"
      ? "#d09338"
      : tone === "down"
      ? "#a1392d"
      : "rgba(29, 32, 25, 0.1)"};
  border-radius: 999px;
  height: 44px;
  opacity: 0.95;
`;

const HistoryLegend = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
`;

const LegendSwatch = styled.span`
  background: ${({ tone }) =>
    tone === "perfect"
      ? "#2e7154"
      : tone === "stable"
      ? "#7aa56f"
      : tone === "shaky"
      ? "#d09338"
      : "#a1392d"};
  border-radius: 999px;
  display: inline-block;
  height: 10px;
  width: 18px;
`;

const LegendText = styled.span`
  color: rgba(29, 32, 25, 0.62);
  font-size: 13px;
`;

const IncidentCard = styled.div`
  background: rgba(29, 32, 25, 0.04);
  border-radius: 16px;
  margin-bottom: 10px;
  padding: 14px 16px;
`;

const IncidentMeta = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const IncidentText = styled.div`
  color: rgba(29, 32, 25, 0.7);
  line-height: 1.5;
`;

const EmptyState = styled.div`
  color: rgba(29, 32, 25, 0.68);
  line-height: 1.5;
`;

export default UptimePage;
